import appConfig from "@/config/app-config";

import type { CaptureContext, SeverityLevel, User } from "@sentry/core";

const SentryEnvironmentMap: { [key: string]: string } = {
  "truthsocial.com": "production",
  "01cxhdz3a8jnmapv.com": "staging",
};

const sentryEnvironment =
  appConfig.NODE_ENV === "production" &&
  Object.keys(SentryEnvironmentMap).find(
    (item) => window.location.hostname.indexOf(item) >= 0,
  );

const denyUrls = [
  // Browser extensions.
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  // Embedded pages.
  /^.*\/embed.*$/i,
];

export const start = (): void => {
  // Skip Sentry initialization for T+ sourced sessions
  if (
    typeof window !== "undefined" &&
    sessionStorage.getItem("truth_plus_source") === "true"
  ) {
    return;
  }

  Promise.all([import("@sentry/react")])
    .then(([Sentry]) => {
      Sentry.init({
        dsn: appConfig.SENTRY_DSN,
        environment: sentryEnvironment
          ? SentryEnvironmentMap[sentryEnvironment]
          : undefined,
        debug: false,
        enabled: !!sentryEnvironment,
        release: appConfig.CI_COMMIT_SHA,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        integrations: [
          Sentry.replayIntegration(),
          Sentry.browserTracingIntegration(),
        ],
        beforeBreadcrumb(breadcrumb) {
          // Ignore fetches to Tabler Icons
          const iconRegex = /^\/packs\/icons\/[\w-]+\.svg$/;
          if (
            breadcrumb.category === "fetch" &&
            iconRegex.test(breadcrumb.data?.url)
          ) {
            return null;
          }

          // Ignore Mixpanel tracking + engaging
          if (
            breadcrumb.category === "xhr" &&
            breadcrumb.data?.url?.includes("https://api-js.mixpanel.com")
          ) {
            return null;
          }

          return breadcrumb;
        },
        beforeSend(event) {
          // If the error comes through via the CaptureConsole integration,
          // the "denyUrls" config option is ignored. We need to manually
          // filter these out.
          // Ref: https://github.com/getsentry/sentry-javascript/issues/5560
          const isDenied = denyUrls.some(
            (url) => !!event.request?.url?.match(url),
          );
          if (isDenied) {
            return null;
          }

          /**
           * Ignore errors that come from external scripts.
           */
          const ignoredFilenames = ["tag/js/gpt.js"];
          if (
            event.exception?.values?.some((value) =>
              value.stacktrace?.frames?.some((frame) =>
                ignoredFilenames.some((ignoredFilename) =>
                  frame.filename?.includes(ignoredFilename),
                ),
              ),
            )
          ) {
            return null; // Ignore the error
          }

          return event;
        },

        // Filter events.
        // https://docs.sentry.io/platforms/javascript/configuration/filtering/
        ignoreErrors: [
          // See: https://github.com/getsentry/sentry-javascript/issues/3440
          "Non-Error exception captured",
          "Non-Error promise rejection captured",
          // Virtuoso quirk with no user impact
          /ResizeObserver loop/i,
          // Network errors.
          /AxiosError/i,
          // sw.js couldn't be downloaded.
          "Failed to update a ServiceWorker for scope",
          // Useful for try/catch, useless as a Sentry error.
          "AbortError",
          // localForage error in FireFox private browsing mode (which doesn't support IndexedDB).
          // We only use IndexedDB as a cache, so we can safely ignore the error.
          "No available storage method found",
          // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
          "originalCreateNotification",
          "canvas.contentDocument",
          "Could not parse the above streaming event.",
          "[object MessageEvent]",
          // Stale chunks after deployment — handled by auto-reload in lazyWithRetry.
          "Failed to fetch dynamically imported module",
          "Importing a module script failed",
          "not a valid JavaScript MIME type",
          "failed to load module script",
          // Edge Mobile bug
          "Can't find variable: logMutedMessage",
          "Can't find variable: gmo",
          "'' is not a valid selector.",
        ],
        denyUrls,
      });
    })
    .catch(console.error);
};

export const captureException = (
  exception: any,
  captureContext?: CaptureContext | undefined,
): Promise<string | undefined> => {
  return import("@sentry/react")
    .then((Sentry) => {
      return Sentry.captureException(exception, captureContext);
    })
    .catch((error) => {
      console.error(error);
      return undefined;
    });
};

export const captureMessage = (
  message: string,
  captureContext?: CaptureContext | SeverityLevel | undefined,
): string | void => {
  import("@sentry/react")
    .then((Sentry) => {
      return Sentry.captureMessage(message, captureContext);
    })
    .catch(console.error);
};

export const setUserToMonitor = (user: User | null): void => {
  import("@sentry/react")
    .then((Sentry) => {
      return Sentry.setUser(user);
    })
    .catch(console.error);
};
